import React, { useState, useEffect } from 'react';
import * as xlsx from 'xlsx';

export const PriceUpdaterModal = ({ token, instanceId, ipAddress, database, onClose }) => {
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [previewData, setPreviewData] = useState([]);
    const [fileId, setFileId] = useState('');
    const [headers, setHeaders] = useState([]);
    const [searchVendor, setSearchVendor] = useState('');
    const [showVendorDropdown, setShowVendorDropdown] = useState(false);
    
    // Mappings
    const [mapping, setMapping] = useState({
        itemCode: '',
        newPrice: '',
        newCost: ''
    });

    const [comparisonResult, setComparisonResult] = useState(null);
    const [backupResult, setBackupResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [executionLog, setExecutionLog] = useState('');
    const [activeTab, setActiveTab] = useState('updater');
    const [backups, setBackups] = useState([]);
    const [executionSuccess, setExecutionSuccess] = useState(false);

    useEffect(() => {
        const fetchVendors = async () => {
            if (!instanceId || !ipAddress || !database) return;
            try {
                const res = await fetch(`/api/price-updater/${instanceId}/${ipAddress}/${database}/vendors`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setVendors(data);
                }
            } catch (err) {
                console.error("Failed to load vendors", err);
            }
        };
        fetchVendors();
    }, [instanceId, ipAddress, database, token]);

    const handleFileUpload = async (e) => {
        const uploadedFile = e.target.files[0];
        if (!uploadedFile) return;

        const formData = new FormData();
        formData.append('file', uploadedFile);

        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/price-updater/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!res.ok) throw new Error('File upload failed');
            const data = await res.json();
            setPreviewData(data.preview);
            setFileId(data.fileId);
            setHeaders(data.headers);
            setComparisonResult(null);
            setBackupResult(null);
            setExecutionLog('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCompare = async () => {
        if (!selectedVendor || !mapping.itemCode || !mapping.newCost || !mapping.newPrice) {
            setError('Please select a vendor and map all columns');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/price-updater/compare`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    instanceId,
                    ipAddress,
                    database,
                    vendorNo: selectedVendor,
                    mapping,
                    fileId
                })
            });
            if (!res.ok) throw new Error('Comparison failed');
            const data = await res.json();
            setComparisonResult(data);
            setBackupResult(null);
        } catch(err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePrepareBackups = async () => {
        if (!comparisonResult) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/price-updater/backup`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    instanceId,
                    ipAddress,
                    database
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Backup generation failed');
            setBackupResult(data);
            setExecutionLog("Backups generated and verified successfully. Please review and confirm execution below.");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmExecute = async () => {
        if (!comparisonResult || !backupResult) return;
        setLoading(true);
        setError(null);
        setExecutionSuccess(false);
        try {
            const res = await fetch(`/api/price-updater/execute`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    instanceId,
                    ipAddress,
                    database,
                    itm_changes: comparisonResult.itm_changes,
                    ven_changes: comparisonResult.ven_changes
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Execution failed');
            setExecutionLog(data.message);
            setBackupResult(null);
            setExecutionSuccess(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchBackups = async () => {
        if (!instanceId || !ipAddress || !database) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/price-updater/${instanceId}/${ipAddress}/${database}/backups`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setBackups(data);
            }
        } catch (err) {
            console.error("Failed to fetch backups", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'backups') {
            fetchBackups();
        }
    }, [activeTab]);

    const handleRestoreBackup = async (tableName) => {
        if (!window.confirm(`Are you sure you want to restore prices from ${tableName}? This will overwrite current prices.`)) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`/api/price-updater/backups/restore`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ instanceId, ipAddress, database, tableName })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Restore failed');
            alert(data.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBackup = async (tableName) => {
        if (!window.confirm(`Are you sure you want to permanently delete backup ${tableName}?`)) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/price-updater/${instanceId}/${ipAddress}/${database}/backups/${tableName}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchBackups();
            }
        } catch (err) {
            console.error("Failed to delete backup", err);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!comparisonResult) return;
        const wb = xlsx.utils.book_new();
        const wsItm = xlsx.utils.json_to_sheet(comparisonResult.itm_changes);
        xlsx.utils.book_append_sheet(wb, wsItm, "ITMFIL00 Changes");
        const wsVen = xlsx.utils.json_to_sheet(comparisonResult.ven_changes);
        xlsx.utils.book_append_sheet(wb, wsVen, "venitm00 Changes");
        xlsx.writeFile(wb, "PriceUpdateReport.xlsx");
    };

    return (
        <div className="flex flex-col h-full bg-slate-900 text-slate-200 p-6 space-y-6 overflow-y-auto w-full">
            <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                <div className="flex items-center gap-6">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-400">price_change</span>
                        Price Update Pipeline
                    </h2>
                    <div className="flex bg-slate-800 rounded p-1">
                        <button 
                            onClick={() => setActiveTab('updater')} 
                            className={`px-4 py-1.5 rounded text-sm font-bold transition-colors ${activeTab === 'updater' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            Updater
                        </button>
                        <button 
                            onClick={() => setActiveTab('backups')} 
                            className={`px-4 py-1.5 rounded text-sm font-bold transition-colors flex items-center gap-1 ${activeTab === 'backups' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">history</span> Backups
                        </button>
                    </div>
                </div>
                <button onClick={onClose} className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-sm font-semibold transition-colors">Close</button>
            </div>

            {error && <div className="p-3 bg-red-900/50 border border-red-500 rounded text-red-200 text-sm">{error}</div>}
            
            {executionSuccess && activeTab === 'updater' && (
                <div className="p-4 bg-green-900/40 border-2 border-green-500 rounded-lg flex items-center gap-3 shadow-lg animate-pulse">
                    <span className="material-symbols-outlined text-green-400 text-3xl">task_alt</span>
                    <div>
                        <h3 className="text-lg font-bold text-green-400">Execution Successful!</h3>
                        <p className="text-green-200">{executionLog}</p>
                    </div>
                </div>
            )}

            {activeTab === 'backups' && (
                <div className="bg-slate-800 p-4 rounded border border-slate-700 shadow flex flex-col gap-4">
                    <h3 className="text-lg font-bold text-blue-300 flex items-center gap-2">
                        <span className="material-symbols-outlined">settings_backup_restore</span> Backup Manager
                    </h3>
                    <p className="text-sm text-slate-400">Manage automated pre-execution backups. Restoring a backup will execute an UPDATE query to revert prices back to their state at the time of backup.</p>
                    
                    <div className="overflow-x-auto mt-4">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="bg-slate-900/50 border-b border-slate-700">
                                    <th className="p-3 font-semibold text-slate-400">Backup Table Name</th>
                                    <th className="p-3 font-semibold text-slate-400">Created At</th>
                                    <th className="p-3 font-semibold text-slate-400 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {backups.map(b => (
                                    <tr key={b.name} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                        <td className="p-3 font-mono text-blue-300">{b.name}</td>
                                        <td className="p-3 text-slate-300">{new Date(b.create_date).toLocaleString()}</td>
                                        <td className="p-3 flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleRestoreBackup(b.name)}
                                                disabled={loading}
                                                className="px-3 py-1 bg-yellow-700 hover:bg-yellow-600 disabled:opacity-50 text-white rounded text-xs font-bold flex items-center gap-1"
                                            >
                                                <span className="material-symbols-outlined text-[14px]">restore</span> Restore
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteBackup(b.name)}
                                                disabled={loading}
                                                className="px-3 py-1 bg-red-900 hover:bg-red-800 disabled:opacity-50 text-white rounded text-xs font-bold flex items-center gap-1"
                                            >
                                                <span className="material-symbols-outlined text-[14px]">delete</span> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {backups.length === 0 && (
                                    <tr><td colSpan="3" className="p-4 text-center text-slate-500 italic">No backups found on server.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'updater' && (
                <>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Configuration Panel */}
                <div className="bg-slate-800 p-4 rounded border border-slate-700 shadow space-y-4">
                    <h3 className="text-lg font-bold text-blue-300">1. Data Ingestion</h3>
                    
                    <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Target Vendor</label>
                        <div className="relative">
                            <input 
                                type="text"
                                placeholder="Search by name or number..."
                                value={searchVendor}
                                onChange={(e) => {
                                    setSearchVendor(e.target.value);
                                    setShowVendorDropdown(true);
                                }}
                                onFocus={() => setShowVendorDropdown(true)}
                                onBlur={() => setTimeout(() => setShowVendorDropdown(false), 200)}
                                className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white outline-none focus:border-blue-500 text-sm"
                            />
                            {showVendorDropdown && (
                                <ul className="absolute z-10 w-full bg-slate-800 border border-slate-600 rounded mt-1 max-h-48 overflow-y-auto shadow-xl">
                                    {vendors.filter(v => 
                                        v.vend_no.toLowerCase().includes(searchVendor.toLowerCase()) || 
                                        v.vend_nam.toLowerCase().includes(searchVendor.toLowerCase())
                                    ).map(v => (
                                        <li 
                                            key={v.vend_no} 
                                            className="px-3 py-2 hover:bg-blue-600 cursor-pointer text-sm text-slate-200"
                                            onClick={() => {
                                                setSelectedVendor(v.vend_no);
                                                setSearchVendor(`${v.vend_no} - ${v.vend_nam}`);
                                                setShowVendorDropdown(false);
                                            }}
                                        >
                                            {v.vend_no} - {v.vend_nam}
                                        </li>
                                    ))}
                                    {vendors.filter(v => 
                                        v.vend_no.toLowerCase().includes(searchVendor.toLowerCase()) || 
                                        v.vend_nam.toLowerCase().includes(searchVendor.toLowerCase())
                                    ).length === 0 && (
                                        <li className="px-3 py-2 text-sm text-slate-400">No vendors found</li>
                                    )}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Spreadsheet Upload</label>
                        <input 
                            type="file" 
                            accept=".csv, .xlsx"
                            onChange={handleFileUpload}
                            className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 text-slate-300 cursor-pointer"
                        />
                    </div>

                    {headers.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-slate-700 mt-4">
                            <h3 className="text-lg font-bold text-blue-300">2. Column Mapping</h3>
                            
                            <div className="space-y-2">
                                {['itemCode', 'newPrice', 'newCost'].map(field => (
                                    <div key={field} className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-medium w-1/3 capitalize">{field.replace('new', 'New ')}</span>
                                        <select 
                                            value={mapping[field]} 
                                            onChange={e => setMapping({...mapping, [field]: e.target.value})}
                                            className="flex-1 bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-white outline-none focus:border-blue-500 text-sm"
                                        >
                                            <option value="">-- Select Column --</option>
                                            {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>

                            <button 
                                onClick={handleCompare}
                                disabled={loading || !selectedVendor}
                                className="w-full mt-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded font-bold transition-colors shadow flex justify-center items-center gap-2"
                            >
                                {loading ? 'Processing...' : 'Run Comparison Analysis'}
                                <span className="material-symbols-outlined text-[18px]">analytics</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Preview Panel */}
                <div className="bg-slate-800 p-4 rounded border border-slate-700 shadow flex flex-col min-h-0">
                    <h3 className="text-lg font-bold text-blue-300 mb-2">Data Preview</h3>
                    {previewData.length > 0 ? (
                        <div className="overflow-x-auto flex-1">
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead>
                                    <tr className="bg-slate-900/50 border-b border-slate-700">
                                        {headers.map(h => <th key={h} className="p-2 font-semibold text-slate-400">{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, idx) => (
                                        <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                            {headers.map(h => <td key={h} className="p-2 text-slate-300">{row[h]}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-slate-500 italic">
                            Upload a file to preview data...
                        </div>
                    )}
                </div>
            </div>

            {/* Results Panel */}
            {comparisonResult && (
                <div className="bg-slate-800 p-4 rounded border border-slate-700 shadow space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold text-blue-300">3. Comparison Report</h3>
                        <div className="flex gap-2">
                            <button onClick={handleExport} className="px-4 py-1.5 bg-green-700 hover:bg-green-600 text-white rounded text-sm font-semibold transition-colors flex items-center gap-1 shadow">
                                <span className="material-symbols-outlined text-[16px]">download</span> Export Report
                            </button>
                            {!backupResult && (
                                <button 
                                    onClick={handlePrepareBackups}
                                    disabled={loading}
                                    className="px-4 py-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-white rounded text-sm font-bold transition-colors flex items-center gap-1 shadow"
                                >
                                    <span className="material-symbols-outlined text-[16px]">backup</span> Prepare Backups
                                </button>
                            )}
                        </div>
                    </div>

                    {backupResult && (
                        <div className="p-4 bg-yellow-900/40 border border-yellow-600 rounded flex flex-col gap-3">
                            <div className="flex items-start gap-2 text-yellow-200">
                                <span className="material-symbols-outlined text-yellow-400">check_circle</span>
                                <div>
                                    <h4 className="font-bold text-yellow-400">Backups Successfully Verified</h4>
                                    <ul className="text-sm list-disc pl-4 mt-1">
                                        <li>{backupResult.itmBackupName} <span className="text-slate-400">({backupResult.itmRows} rows)</span></li>
                                        <li>{backupResult.venBackupName} <span className="text-slate-400">({backupResult.venRows} rows)</span></li>
                                    </ul>
                                </div>
                            </div>
                            <button 
                                onClick={handleConfirmExecute}
                                disabled={loading}
                                className="w-full py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded font-bold transition-colors shadow flex justify-center items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">warning</span> 
                                Confirm and Apply Prices
                            </button>
                        </div>
                    )}

                    {executionLog && (
                        <div className="p-3 bg-green-900/50 border border-green-500 rounded text-green-200 font-mono text-sm">
                            {executionLog}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-slate-900 border border-slate-700 rounded p-3 h-64 overflow-y-auto custom-scrollbar">
                            <h4 className="font-bold text-slate-300 mb-2 sticky top-0 bg-slate-900 py-1">
                                ITMFIL00 Changes ({comparisonResult.itm_changes.length})
                            </h4>
                            <ul className="text-xs space-y-1">
                                {comparisonResult.itm_changes.map((c, i) => (
                                    <li key={i} className="flex justify-between border-b border-slate-800 pb-1">
                                        <span className="font-mono text-blue-300">{c.item_no}</span>
                                        <span className="text-slate-400 text-right">
                                            Prc: {c.old_price} &rarr; <span className="text-green-400">{c.new_price}</span> <br/>
                                            Cst: {c.old_cost} &rarr; <span className="text-green-400">{c.new_cost}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-slate-900 border border-slate-700 rounded p-3 h-64 overflow-y-auto custom-scrollbar">
                            <h4 className="font-bold text-slate-300 mb-2 sticky top-0 bg-slate-900 py-1">
                                venitm00 Changes ({comparisonResult.ven_changes.length})
                            </h4>
                            <ul className="text-xs space-y-1">
                                {comparisonResult.ven_changes.map((c, i) => (
                                    <li key={i} className="flex justify-between border-b border-slate-800 pb-1">
                                        <span className="font-mono text-blue-300">{c.item_no}</span>
                                        <span className="text-slate-400 text-right">
                                            Cst: {c.old_cost} &rarr; <span className="text-green-400">{c.new_cost}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
            </>
            )}
        </div>
    );
};
